/**
 * Helpers for the Gmail-style local message drafts (Issue #206).
 *
 * Drafts are stored in localStorage under the following keys:
 *   - kars:draft:compose:{orgId}:{userId}:{caseId}:{draftId} → "Neue Nachricht"-Entwurf je Fenster
 *   - kars:draft:compose:{orgId}:{userId}:{caseId}           → Alt-Format (ein Entwurf je Fall)
 *   - kars:draft:reply:{orgId}:{userId}:{caseId}:{convId}    → Antwort-Entwurf je Konversation
 *
 * Kundenfeedback 26.10.2026, V: Ein neues E-Mail-Fenster startet immer leer.
 * Entwürfe öffnet man bewusst über „Entwürfe“; jedes Fenster speichert unter
 * einer eigenen Kennung (draftId), damit eine neue E-Mail zum selben Fall
 * einen vorhandenen Entwurf weder lädt noch überschreibt.
 *
 * Die Keys sind pro Organisation UND Benutzer namespaced (Kundenreview
 * 23.08.26, XVI): ohne Namespace sah ein Benutzer nach dem Wechsel in ein
 * anderes Unternehmen die Entwürfe des vorherigen Mandanten. Alt-Keys ohne
 * Namespace werden ignoriert und beim nächsten Scan entfernt.
 *
 * This module exposes a single `listDrafts()` helper that scans localStorage
 * and returns a normalised list, used by the "Entwürfe" tab in
 * SectionNachrichten (Fall-Detail) und auf der globalen /nachrichten-Seite.
 */

import { useAuthStore } from "@/stores/authStore";

export type DraftKind = "compose" | "reply";

export interface DraftExternal {
  email: string;
  name: string;
  role: string;
}

export interface MessageDraft {
  /** Full localStorage key, used to delete the draft. */
  key: string;
  kind: DraftKind;
  caseId: number;
  /** Conversation id for reply drafts, null for compose drafts. */
  convId: number | null;
  subject: string;
  /** Plain-text excerpt of the draft body (HTML stripped). */
  preview: string;
  /** Recipients for compose drafts (mix of user-ids and "external:..."). */
  recipients: string[];
  /** Externe Empfänger (E-Mail) eines "Neue Nachricht"-Entwurfs. */
  externals: DraftExternal[];
  /** Epoch milliseconds of the last save, 0 if unknown. */
  savedAt: number;
}

const COMPOSE_PREFIX = "kars:draft:compose:";
const REPLY_PREFIX = "kars:draft:reply:";

/**
 * Tenant namespace for draft keys: "{orgId}:{userId}".
 * Returns null when no user is logged in — draft helpers then no-op safely.
 */
function draftNamespace(): string | null {
  try {
    const auth = useAuthStore();
    const orgId = Number(auth.idOrganization);
    const userId = Number(auth.idUser);
    if (!orgId || !userId) return null;
    return `${orgId}:${userId}`;
  } catch {
    return null;
  }
}

function stripHtml(html: string | null | undefined): string {
  if (!html) return "";
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function readExternals(raw: unknown): DraftExternal[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((e) => e && typeof e === "object" && typeof e.email === "string" && e.email.trim())
    .map((e) => ({ email: e.email.trim(), name: String(e.name || ""), role: String(e.role || "to") }));
}

function isEmptyComposeDraft(preview: string, subject: string, recipients: unknown[], externals: DraftExternal[]): boolean {
  return !preview && !subject && recipients.length === 0 && externals.length === 0;
}

function safeParse(raw: string | null): any {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function composeDraftKey(caseId: number | string, draftId?: string | null): string {
  const ns = draftNamespace();
  const base = `${COMPOSE_PREFIX}${ns ?? "0:0"}:${caseId}`;
  return draftId ? `${base}:${draftId}` : base;
}

/** Inhalt eines "Neue Nachricht"-Entwurfs, wie ihn das E-Mail-Fenster sichert. */
export interface ComposeDraftData {
  subject: string;
  content: string;
  recipients: unknown[];
  externals: DraftExternal[];
  showSignature: boolean;
  signatureLocationId: number | null;
  isUrgent: boolean;
  requestDeliveryReceipt: boolean;
  requestReadReceipt: boolean;
  savedAt: number;
}

/** Neue Kennung für den Entwurf eines E-Mail-Fensters. */
export function newComposeDraftId(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

/** Kennung (draftId) aus einem Entwurfs-Schlüssel; null beim Alt-Format. */
export function composeDraftIdFromKey(key: string): string | null {
  if (!key.startsWith(COMPOSE_PREFIX)) return null;
  const parts = key.substring(COMPOSE_PREFIX.length).split(":");
  return parts.length >= 4 && parts[3] ? parts[3] : null;
}

/**
 * Welchen Entwurf soll „Neue Nachricht“ laden? Nur den ausdrücklich
 * übergebenen (`draftKey` aus dem Ordner „Entwürfe“) – sonst keinen, auch
 * wenn zum Fall Entwürfe existieren (Kundenfeedback 26.10.2026, V).
 */
export function requestedComposeDraftKey(opts: Record<string, unknown> | null | undefined): string | null {
  const key = opts?.draftKey;
  return typeof key === "string" && key.startsWith(COMPOSE_PREFIX) ? key : null;
}

/**
 * Entwurf lesen, den der Nutzer bewusst öffnet (Ordner „Entwürfe“).
 * null, wenn er fehlt, defekt oder leer ist.
 */
export function readComposeDraft(key: string): ComposeDraftData | null {
  if (!key.startsWith(COMPOSE_PREFIX)) return null;
  let data: any = null;
  try {
    data = safeParse(localStorage.getItem(key));
  } catch {
    return null;
  }
  if (!data || typeof data !== "object") return null;
  const subject = typeof data.subject === "string" ? data.subject : "";
  const content = typeof data.content === "string" ? data.content : "";
  const recipients = Array.isArray(data.recipients) ? data.recipients : [];
  const externals = readExternals(data.externals);
  if (isEmptyComposeDraft(stripHtml(content), subject.trim(), recipients, externals)) return null;
  return {
    subject,
    content,
    recipients,
    externals,
    showSignature: data.showSignature !== false,
    signatureLocationId: data.signatureLocationId ?? null,
    isUrgent: data.isUrgent === true,
    requestDeliveryReceipt: data.requestDeliveryReceipt === true,
    requestReadReceipt: data.requestReadReceipt === true,
    savedAt: Number(data.savedAt) || 0,
  };
}

/**
 * Entwurf eines E-Mail-Fensters sichern. Liegt er bisher unter einem anderen
 * Schlüssel (Fall gewechselt, Alt-Format), wird der alte Eintrag entfernt –
 * so bleibt pro Fenster genau ein Entwurf. Liefert den neuen Schlüssel oder
 * null, wenn nicht gespeichert werden konnte.
 */
export function saveComposeDraft(opts: {
  previousKey: string | null;
  caseId: number | string;
  draftId: string;
  data: ComposeDraftData;
}): string | null {
  if (!draftNamespace()) return null;
  const key = composeDraftKey(opts.caseId || 0, opts.draftId);
  try {
    localStorage.setItem(key, JSON.stringify(opts.data));
  } catch {
    /* Speicher voll oder gesperrt – der Entwurf ist dann nur nicht gesichert. */
    return opts.previousKey;
  }
  if (opts.previousKey && opts.previousKey !== key) deleteDraft(opts.previousKey);
  return key;
}

export function replyDraftKey(caseId: number | string, convId: number | string): string {
  const ns = draftNamespace();
  return `${REPLY_PREFIX}${ns ?? "0:0"}:${caseId}:${convId}`;
}

/**
 * Scan localStorage for all message drafts of the CURRENT org + user.
 *
 * Legacy keys without namespace (kars:draft:compose:{caseId}) are removed on
 * sight: they cannot be attributed to a tenant and must never surface in
 * another company's mailbox again.
 *
 * @param caseIdFilter optional — return only drafts that belong to this case
 *                     (used by the case-detail Nachrichten section).
 */
export function listDrafts(caseIdFilter?: number): MessageDraft[] {
  if (typeof window === "undefined" || !window.localStorage) return [];
  const ns = draftNamespace();
  if (!ns) return [];
  const out: MessageDraft[] = [];
  const staleKeys: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;

    if (key.startsWith(COMPOSE_PREFIX)) {
      // Expected segments: {orgId}:{userId}:{caseId}[:{draftId}]
      const parts = key.substring(COMPOSE_PREFIX.length).split(":");
      if (parts.length < 3) {
        staleKeys.push(key);
        continue;
      }
      if (`${parts[0]}:${parts[1]}` !== ns) continue;
      const caseId = parseInt(parts[2] ?? "", 10);
      if (!Number.isFinite(caseId)) continue;
      if (caseIdFilter !== undefined && caseId !== caseIdFilter) continue;
      const data = safeParse(localStorage.getItem(key));
      if (!data) continue;
      const preview = stripHtml(data.content);
      const subject = (data.subject || "").trim();
      const recipients = Array.isArray(data.recipients) ? data.recipients : [];
      const externals = readExternals(data.externals);
      if (isEmptyComposeDraft(preview, subject, recipients, externals)) continue;
      out.push({
        key,
        kind: "compose",
        caseId,
        convId: null,
        subject,
        preview,
        recipients,
        externals,
        savedAt: Number(data.savedAt) || 0,
      });
    } else if (key.startsWith(REPLY_PREFIX)) {
      // Expected segments: {orgId}:{userId}:{caseId}:{convId}
      const parts = key.substring(REPLY_PREFIX.length).split(":");
      if (parts.length < 4) {
        staleKeys.push(key);
        continue;
      }
      if (`${parts[0]}:${parts[1]}` !== ns) continue;
      const caseId = parseInt(parts[2] ?? "", 10);
      const convId = parseInt(parts[3] ?? "", 10);
      if (!Number.isFinite(caseId) || !Number.isFinite(convId)) continue;
      if (caseIdFilter !== undefined && caseId !== caseIdFilter) continue;
      const data = safeParse(localStorage.getItem(key));
      if (!data) continue;
      const preview = stripHtml(data.content);
      if (!preview) continue;
      out.push({
        key,
        kind: "reply",
        caseId,
        convId,
        subject: "",
        preview,
        recipients: [],
        externals: [],
        savedAt: Number(data.savedAt) || 0,
      });
    }
  }

  for (const key of staleKeys) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* noop */
    }
  }

  // Newest first.
  out.sort((a, b) => b.savedAt - a.savedAt);
  return out;
}

export function deleteDraft(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* noop */
  }
}

/**
 * Antwort-Entwurf einer Unterhaltung sichern (Chat-Verlauf, 01.10.2026).
 * Leerer Inhalt entfernt den Entwurf – eine Antwort, die nur noch aus der
 * Signatur besteht, gibt der Aufrufer als "" weiter.
 */
export function saveReplyDraft(caseId: number | string, convId: number | string, html: string): void {
  if (!draftNamespace()) return;
  const key = replyDraftKey(caseId || 0, convId);
  try {
    if (!stripHtml(html)) {
      localStorage.removeItem(key);
      return;
    }
    localStorage.setItem(key, JSON.stringify({ content: html, savedAt: Date.now() }));
  } catch {
    /* Speicher voll oder gesperrt – der Entwurf ist dann nur nicht gesichert. */
  }
}

/** Gesicherten Antwort-Entwurf lesen ("" wenn keiner vorhanden ist). */
export function readReplyDraft(caseId: number | string, convId: number | string): string {
  if (!draftNamespace()) return "";
  const data = safeParse(localStorage.getItem(replyDraftKey(caseId || 0, convId)));
  return typeof data?.content === "string" ? data.content : "";
}
